import { useMemo, useState } from 'react';
import css from './OfferForm.module.css';
import {
  composeValidators,
  moneySubUnitAmountAtLeast,
  required,
} from '../../../../util/validators';
import { Form, PrimaryButton, FieldCurrencyInput, Icons } from '../../../../components';
import appSettings from '../../../../config/settings';
import { Form as FinalForm } from 'react-final-form';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import classNames from 'classnames';
import { formatMoney } from '../../../../util/currency';
import { FormattedMessage } from 'react-intl';

const { Money } = sdkTypes;

const OfferForm = props => {
  const { listingTitle } = props;

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          listing,
          config,
          sendOfferError,
          form,
          values,
        } = fieldRenderProps;

        const marketplaceCurrency = config.currency;
        const { formatMessage } = intl;
        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;

        // Listing price
        const listingPrice =
          listing?.attributes?.price instanceof Money ? listing.attributes.price : null;
        const listingAmount = listingPrice?.amount ?? null;

        // Per-listing minimum offer (SUBUNITS). If undefined/null, no threshold is enforced.
        const listingMinOfferSubunits =
          typeof listing?.attributes?.publicData?.minOffer === 'number'
            ? listing.attributes.publicData.minOffer
            : null;

        // Global minimum listing price validator (kept)
        const priceRequired = required(formatMessage({ id: 'OfferForm.priceRequired' }));
        const minListingPrice = new Money(config.listingMinimumPriceSubUnits, marketplaceCurrency);
        const minListingPriceRequired = moneySubUnitAmountAtLeast(
          formatMessage(
            { id: 'OfferForm.priceTooLow' },
            { minPrice: formatMoney(intl, minListingPrice) }
          ),
          config.listingMinimumPriceSubUnits
        );

        const priceValidators = config.listingMinimumPriceSubUnits
          ? composeValidators(priceRequired, minListingPriceRequired)
          : priceRequired;

        // Current price (Money) and subunits
        const priceMoney = values?.price instanceof Money ? values.price : null;
        const priceSubunits = typeof priceMoney?.amount === 'number' ? priceMoney.amount : null;

        // Below listing-specific minimum offer?
        const belowListingMin =
          typeof listingMinOfferSubunits === 'number' &&
          typeof priceSubunits === 'number' &&
          priceSubunits < listingMinOfferSubunits;

        // Submit disabled?
        const submitDisabled = submitInProgress || invalid || belowListingMin;

        // Recommendations (5% & 10% off)
        const recommendations = useMemo(() => {
          if (typeof listingAmount !== 'number') return [];
          const sub = listingAmount;
          const fiveOff = Math.max(0, Math.round(sub * 0.95));
          const tenOff = Math.max(0, Math.round(sub * 0.9));
          return [
            {
              key: '5',
              label: formatMessage({ id: 'OfferForm.fivePercentOff' }),
              subunits: fiveOff,
            },
            {
              key: '10',
              label: formatMessage({ id: 'OfferForm.tenPercentOff' }),
              subunits: tenOff,
            },
          ];
        }, [listingAmount, formatMessage]);

        const [activeChip, setActiveChip] = useState('custom');

        const setPriceFromSubunits = sub => {
          if (typeof sub !== 'number' || !marketplaceCurrency) return;
          form.change('price', new Money(sub, marketplaceCurrency));
        };

        const onSelectRecommendation = rec => {
          setActiveChip(rec.key);
          setPriceFromSubunits(rec.subunits);
        };
        const onSelectCustom = () => setActiveChip('custom');

        // Buyer-protection fee and total
        const buyerProtection = {
          percent:
            typeof config?.buyerProtectionPercent === 'number'
              ? config.buyerProtectionPercent
              : 0.11,
          fixedSubunits:
            typeof config?.buyerProtectionFixedSubunits === 'number'
              ? config.buyerProtectionFixedSubunits
              : 0,
          minSubunits:
            typeof config?.buyerProtectionMinSubunits === 'number'
              ? config.buyerProtectionMinSubunits
              : 0,
        };

        const feeSubunits = useMemo(() => {
          if (typeof priceSubunits !== 'number') return 0;
          const pctPart = Math.round(priceSubunits * (buyerProtection.percent || 0));
          const raw = pctPart + (buyerProtection.fixedSubunits || 0);
          return Math.max(raw, buyerProtection.minSubunits || 0);
        }, [priceSubunits, buyerProtection]);

        const totalSubunits =
          typeof priceSubunits === 'number' ? priceSubunits + feeSubunits : null;

        // Only show total when a recommendation is active or custom has value
        const showTotal =
          activeChip === '5' ||
          activeChip === '10' ||
          (activeChip === 'custom' && typeof priceSubunits === 'number');

        const fmt = sub => formatMoney(intl, new Money(Math.max(0, sub || 0), marketplaceCurrency));

        const heroImg =
          listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url ||
          listing?.images?.[0]?.attributes?.variants?.['scaled-medium']?.url;

        const brandName =
          (config && (config.siteTitle || config.marketplaceName)) || 'Marketplace';

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.listingDetails}>
              <div className={css.listingImg}>
                <img src={heroImg} alt={listingTitle} />
              </div>
              <div className={css.listingInfo}>
                <h2>{listingTitle}</h2>
                <p>
                  <FormattedMessage id="OfferForm.itemPrice" />:{' '}
                  {listingPrice ? fmt(listingPrice.amount) : '$4.21'}
                </p>

                {/* Intentionally not rendering minOffer note per request */}
              </div>
            </div>

            <div className={css.setOfferPriceSec}>
              <div className={css.offerPriceSec}>
                {recommendations.map(rec => (
                  <button
                    key={rec.key}
                    type="button"
                    className={classNames(css.priceBlock, activeChip === rec.key && css.active)}
                    onClick={() => onSelectRecommendation(rec)}
                  >
                    <h2>{fmt(rec.subunits)}</h2>
                    <p>{rec.label}</p>
                  </button>
                ))}
                <button
                  type="button"
                  className={classNames(css.priceBlock, activeChip === 'custom' && css.active)}
                  onClick={onSelectCustom}
                >
                  <h2>
                    <FormattedMessage id="OfferForm.custom" />
                  </h2>
                  <p>
                    <FormattedMessage id="OfferForm.setAPrice" />
                  </p>
                </button>
              </div>

              <FieldCurrencyInput
                id="price"
                name="price"
                className={css.input}
                label={formatMessage({ id: 'OfferForm.priceLabel' })}
                placeholder={formatMessage({ id: 'OfferForm.priceInputPlaceholder' })}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                validate={priceValidators}
                autoFocus
              />

              {belowListingMin ? (
                <p className={css.error}>
                  <FormattedMessage
                    id="OfferForm.minimumOfferError"
                    values={{ minOffer: fmt(listingMinOfferSubunits) }}
                  />
                </p>
              ) : null}

              {showTotal && typeof totalSubunits === 'number' ? (
                <p className={css.totalNote}>
                  <FormattedMessage
                    id="OfferForm.totalWithProtection"
                    values={{ total: fmt(totalSubunits), fee: fmt(feeSubunits) }}
                  />
                </p>
              ) : null}
            </div>

            <div className={css.actionBtns}>
              {sendOfferError ? (
                <p className={css.error}>
                  {formatMessage({ id: 'OfferForm.sendEnquiryError' })}
                </p>
              ) : null}
              <PrimaryButton
                className={css.submitBtn}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                {formatMessage({ id: 'OfferForm.submitButtonText' })}
              </PrimaryButton>
            </div>

            <div className={css.protectionSec}>
              <h2>
                <Icons name="whiteLock" /> <FormattedMessage id="OfferForm.buyerProtectionTitle" />
              </h2>
              <p>
                <FormattedMessage
                  id="OfferForm.buyerProtectionBody"
                  values={{ brandName }}
                />
              </p>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default OfferForm;
